import type { TeamHistoryOfficialView, TeamHistoryTerms } from '../types'

/**
 * Strings for the selected team's summary card. Headings and labels are lower
 * case, for `capitalizeWords` at the render site; captions and the empty-state
 * sentence are sentence case and render as they are.
 */
export interface TeamSummaryCopy {
  competitions: string
  competitionsCaption: string
  players: string
  playersCaption: string
  firstPlayed: string
  firstPlayedCaption: string
  lastPlayed: string
  lastPlayedCaption: string
  lastCompetition: string
  teamOfficials: string
  teamOfficialsCaption: string
  noHistoryTitle: string
  noHistoryBody: string
}

/**
 * UI strings for team history, worded with the tenant's competition noun
 * ("competition" / "season"). Labels come back in source case — wrap them in
 * `capitalizeWords` at the render site.
 */
export interface TeamHistoryCopy {
  noHistory: string
  treasurer: string
  summary: TeamSummaryCopy
}

export const buildTeamHistoryCopy = ({
  competition,
  player,
}: TeamHistoryTerms): TeamHistoryCopy => ({
  noHistory: `No ${competition.singular} history yet`,
  treasurer: 'treasurer',
  summary: {
    competitions: competition.plural,
    competitionsCaption: 'Played to date',
    players: player.plural,
    playersCaption: 'On last roster',
    firstPlayed: 'first played',
    firstPlayedCaption: `First ${competition.singular}`,
    lastPlayed: 'last played',
    lastPlayedCaption: `Most recent ${competition.singular}`,
    lastCompetition: `last ${competition.singular}`,
    teamOfficials: 'team officials',
    teamOfficialsCaption: 'From last roster',
    noHistoryTitle: `first ${competition.singular}`,
    noHistoryBody: `This team hasn't played a ${competition.singular} yet, so there's no history, roster or officials to show.`,
  },
})

/** "Since Apr 2026". */
export const sinceLabel = (monthYear: string): string => `Since ${monthYear}`

/** One line naming a competition and where it ran: "Winter Hockey · ASHL Oshawa". */
export const joinWithDot = (...parts: Array<string | undefined>): string =>
  parts.filter(Boolean).join(' · ')

/** Hover text for an official's avatar: "Pouya Litkouhi · Manager · Treasurer". */
export const describeOfficial = (
  official: TeamHistoryOfficialView,
  treasurerLabel: string
): string =>
  joinWithDot(
    official.fullName,
    official.roleName,
    official.isTreasurer ? treasurerLabel : undefined
  )
